import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Paper,
  InputAdornment,
  Snackbar,
  Alert,
  Divider,
  Skeleton,
  Slide,
  Fade,
  Radio,
  RadioGroup,
  FormControlLabel,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline
} from "@mui/material";
import { styled as muiStyled } from '@mui/material/styles';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import styled from "styled-components";
import { useAuthToken } from "../TokenContext";
import { useDispatch, useSelector } from "react-redux";
import { login } from "./action/auth";
import axios from "axios";
import { useIntl } from "react-intl";
import './controlplane/ArtemyStyles.css';

// Modern styled components
// Updated StyledCard component with better dark mode support

const StyledCard = muiStyled(Card)(({ theme, featured }) => ({
  borderRadius: 24,
  boxShadow: 'none',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  position: 'relative',
  overflow: 'hidden',
  border: featured ? '2px solid var(--artemy-accent)' : '1px solid var(--artemy-border)',
  height: '100%',
  backgroundColor: 'var(--artemy-bg-card)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'var(--artemy-accent)',
    boxShadow: '0 12px 40px rgba(61, 127, 255, 0.2)',
  }
}));

const CardHeader = muiStyled(Box)(({ theme, bgcolor }) => ({
  padding: '32px 24px',
  background: 'rgba(255, 255, 255, 0.03)',
  borderBottom: '1px solid var(--artemy-border)',
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
}));

const PriceTag = muiStyled(Typography)(({ theme }) => ({
  fontSize: 32, // Reduced from 42 for better balance
  fontWeight: 800,
  color: 'var(--artemy-text-primary)',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'baseline',
  fontFamily: 'var(--artemy-font-display)',
}));

const InvoiceList = muiStyled(Paper)(({ theme }) => ({
  borderRadius: 20,
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  maxHeight: '500px',
  overflowY: 'auto',
  backgroundColor: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  marginTop: 32,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

const InvoiceHeader = muiStyled(Box)(({ theme }) => ({
  padding: '20px 24px',
  background: 'var(--artemy-bg-elevated)',
  borderBottom: '1px solid var(--artemy-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const InvoiceItem = muiStyled(Box)(({ theme }) => ({
  padding: '16px 24px',
  borderBottom: '1px solid var(--artemy-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
  '&:last-child': {
    borderBottom: 'none',
  }
}));

const CardBody = muiStyled(Box)(({ theme }) => ({
  padding: '32px 24px',
  backgroundColor: 'transparent',
}));

const CardFooter = muiStyled(Box)(({ theme }) => ({
  padding: '24px',
  borderTop: '1px solid var(--artemy-border)',
  backgroundColor: 'rgba(255, 255, 255, 0.01)',
  display: 'flex',
  justifyContent: 'center',
}));

// Update FeatureItem with better dark mode support
const FeatureItem = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 12,
  color: 'var(--artemy-text-primary)',
  '&:last-child': {
    marginBottom: 0,
  }
}));

// Updated styles for the Current Plan section in dark mode to match screenshot exactly
const CurrentPlanWrapper = muiStyled(Paper)(({ theme }) => ({
  padding: 0, 
  borderRadius: 24,
  overflow: 'hidden',
  border: '1px solid var(--artemy-border)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  backgroundColor: 'var(--artemy-bg-card)',
  backdropFilter: 'blur(10px)',
}));

const CurrentPlanHeader = muiStyled(Box)(({ theme }) => ({ 
  padding: '24px 32px',
  background: 'var(--artemy-accent-gradient)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 4px 20px rgba(61, 127, 255, 0.3)',
}));

const CurrentPlanContent = muiStyled(Box)(({ theme }) => ({ 
  padding: '32px', 
  backgroundColor: 'transparent' 
}));

const CurrentPlanLabel = muiStyled(Typography)(({ theme }) => ({
  variant: 'subtitle2', 
  fontWeight: 700,
  color: 'var(--artemy-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '4px',
  fontFamily: 'var(--artemy-font-mono)',
}));

const CurrentPlanValue = muiStyled(Typography)(({ theme }) => ({
  variant: 'h6',
  color: 'var(--artemy-text-primary)',
  fontWeight: 600,
  fontFamily: 'var(--artemy-font-body)',
}));

// Updated billing history section for dark mode
const BillingHistoryTitle = muiStyled(Typography)(({ theme }) => ({
  color: 'var(--artemy-text-primary)',
  display: 'flex',
  alignItems: 'center',
  fontSize: 24,
  fontWeight: 700,
  fontFamily: 'var(--artemy-font-display)',
  marginBottom: 24,
}));

const PageContainer = muiStyled(Box)(({ theme }) => ({
  maxWidth: 1400,
  margin: '0 auto',
  padding: '40px 20px',
  backgroundColor: 'transparent',
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  overflowX: 'hidden',
  
  /* Hide scrollbar but allow scrolling */
  scrollbarWidth: 'none', /* Firefox */
  msOverflowStyle: 'none', /* IE and Edge */
  '&::-webkit-scrollbar': {
    display: 'none', /* Chrome, Safari, Opera */
  },
  
  [theme.breakpoints.down('md')]: {
    padding: '20px 16px',
  }
}));

const PageHeader = muiStyled(Box)(({ theme }) => ({
  marginBottom: 40,
  textAlign: 'left',
}));

const MainTitle = muiStyled(Typography)(({ theme }) => ({
  fontSize: 36,
  fontWeight: 800,
  marginBottom: 8,
  color: 'var(--artemy-text-primary)',
  fontFamily: "var(--artemy-font-display)",
  letterSpacing: '-0.5px',
}));

const SubTitle = muiStyled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: 'var(--artemy-text-secondary)',
  fontWeight: 600,
  marginTop: 0,
  fontFamily: "var(--artemy-font-mono)",
  textTransform: 'uppercase',
  letterSpacing: '2px',
  opacity: 0.8,
}));

const ContentGrid = muiStyled(Grid)(({ theme }) => ({
  gap: 30,
  
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
  }
}));

// Convert styled-components to MUI styled components with dark mode support
const PlansSection = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : 'inherit',
}));

const PlanCards = muiStyled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '20px',
  marginBottom: '30px',
  maxHeight: '60vh',
  overflowY: 'auto',
  
  /* Hide scrollbar but allow scrolling */
  scrollbarWidth: 'none', /* Firefox */
  msOverflowStyle: 'none', /* IE and Edge */
  '&::-webkit-scrollbar': {
    display: 'none', /* Chrome, Safari, Opera */
  },
}));

const SidebarSection = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : 'inherit',
}));

const PlanBadge = muiStyled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  padding: '6px 12px',
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
  backgroundColor: 'var(--artemy-accent)',
  color: 'white',
  zIndex: 1,
}));

const PriceUnit = muiStyled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  color: '#64748b',
  marginLeft: 4,
}));

const FeaturesList = muiStyled(Box)(({ theme }) => ({
  marginTop: 16,
  marginBottom: 24,
}));

const ActionButton = muiStyled(Button)(({ theme, variant }) => ({
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: 16,
  width: '100%',
  backgroundColor: variant === 'contained' ? 'var(--artemy-accent)' : 'transparent',
  color: variant === 'contained' ? '#000' : 'var(--artemy-accent)',
  border: variant === 'outlined' ? '1px solid var(--artemy-accent)' : 'none',
  '&:hover': {
    backgroundColor: variant === 'contained' ? 'var(--artemy-accent-bright)' : 'rgba(var(--artemy-accent-rgb), 0.1)',
  }
}));

const CreditCardBox = muiStyled(Paper)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(135deg, #0396FF, #0D47A1)',
  color: 'white',
  padding: 0,
  height: '100%',
}));

const CardChip = muiStyled(Box)(({ theme }) => ({
  width: 50,
  height: 40,
  borderRadius: 8,
  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
  marginBottom: 20,
}));

const DialogForm = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

const TabsContainer = muiStyled(Box)(({ theme }) => ({
  borderBottom: '1px solid #e0e7ff',
  marginBottom: 24,
}));

const StyledTabs = muiStyled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: 'var(--artemy-accent)',
    height: 3,
  }
}));

const StyledTab = muiStyled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 16,
  padding: '12px 24px',
  '&.Mui-selected': {
    color: 'var(--artemy-accent)',
  }
}));

const CheckoutContainer = muiStyled(Box)(({ theme }) => ({
  maxWidth: 800,
  margin: '0 auto',
  padding: 32,
  borderRadius: 16,
  backgroundColor: 'white',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const CheckoutStep = muiStyled(Box)(({ theme }) => ({
  marginBottom: 32,
  '&:last-child': {
    marginBottom: 0,
  }
}));

const StepTitle = muiStyled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  marginBottom: 16,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: 8,
  }
}));

const CheckoutActions = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 24,
  gap: 16,
}));

const CheckoutSummary = muiStyled(Paper)(({ theme }) => ({
  padding: 24,
  borderRadius: 12,
  backgroundColor: '#f8fafc',
  marginTop: 24,
}));

const SummaryRow = muiStyled(Box)(({ theme, isBold }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 12,
  fontWeight: isBold ? 600 : 400,
  '&:last-child': {
    marginBottom: 0,
  }
}));

/*
 * ENHANCED SUBSCRIPTION COMPONENT WITH PROVIDER SUPPORT
 * 
 * This component handles subscription management for both:
 * 1. PROVIDERS: Uses getPartner API for data refresh
 * 2. CUSTOMERS: Uses get_candidate API for data refresh
 * 
 * Key Features:
 * - Different API endpoints for provider vs customer subscription creation
 * - Proper data refresh after subscription actions (create/cancel)
 * - Provider-specific field names (subscriptionstatus, partner_ID, partner_code)
 * - Customer-specific field names (Status, client_ID, organizational_code)
 * - Real-time subscription status updates
 * - Manual refresh capability
 * - Comprehensive error handling and user feedback
 * 
 * API Endpoints Used:
 * - Provider Subscription: https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/partner-subscription-create-db
 * - Provider Data Refresh: https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/getPartner
 * - Customer Subscription: REACT_APP_CLIENT_CREATESUBSCRIPTIONDB
 * - Customer Data Refresh: Via login dispatch (get_candidate API)
 */

// Enhanced Subscription Component
const SubscriptionNew = () => {
  // States from the original component
  const [plans, setPlans] = useState([]);
  const [showAddCardDialog, setShowAddCardDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showExistingCardDialog, setShowExistingCardDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [cardDetails, setCardDetails] = useState(null);
  const [newCard, setNewCard] = useState({
    number: "",
    name: "",
    exp_month: "",
    exp_year: "",
    cvc: "",
    postal_code: "",
  });
  const [showEditCardDialog, setShowEditCardDialog] = useState(false);
  const token = useAuthToken();
  const [isPartner, setIsPartner] = useState(false);
  const { isLoggedIn, user } = useSelector((state) => state.auth);
  const [createCustomerId, setCreateCustomerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [APIresponse, setAPIresponse] = useState(null);
  const [paymentID, setPaymentID] = useState(null);
  const [step, setStep] = useState(null);
  const [addCard, setAddCard] = useState(false);
  const dispatch = useDispatch();
  const [getpoddetails, setGetpoddetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [useExistingCard, setUseExistingCard] = useState(false);
  const [currentPlan, setCurrentPlan] = useState({
    id: "",
    price: 0,
    balance: 1,
    type: "Web App",
    Product_Name: '',
  });
  const intl = useIntl();
  
  const [numberOfPersons, setNumberOfPersons] = useState(1);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [totalAmount, setTotalAmount] = useState(0);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [responseBody, setResponseBody] = useState(null);
  const [showSecondCard, setShowSecondCard] = useState(false);
  const [totalAmountWithTax, setTotalAmountWithTax] = useState(0);
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Additional enhanced states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [cardNumberMasked, setCardNumberMasked] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [refreshingData, setRefreshingData] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null); // Track which specific plan is loading
  const [confirmingSubscription, setConfirmingSubscription] = useState(false); // Track confirm button loading
  const [subscriptionDataLoading, setSubscriptionDataLoading] = useState(true); // Track if subscription data is being loaded

  // UPDATED: Helper function to get subscription data based on user type
  const getSubscriptionData = () => {
    if (user.data[0].type === "Provider") {
      // For providers, use subscriptiondata array with provider field names
      if (user.subscriptiondata && user.subscriptiondata.length > 0) {
        // Find active subscription or return the most recent one
        const activeSubscription = user.subscriptiondata.find(sub => sub.subscriptionstatus === "active");
        return activeSubscription || user.subscriptiondata[0];
      }
      return null;
    } else {
      // For customers, use subscriptiondata array with customer field names
      if (user.subscriptiondata && user.subscriptiondata.length > 0) {
        const activeSubscription = user.subscriptiondata.find(sub => sub.Status === "active");
        return activeSubscription || user.subscriptiondata[0];
      }
      return null;
    }
  };

  // UPDATED: Helper function to check if user has active subscription (with loading state)
  const hasActiveSubscription = () => {
    // While loading, assume no active subscription to show "Choose Plan" buttons
    if (subscriptionDataLoading) {
      return false;
    }
    
    if (user.data[0].type === "Provider") {
      return user.subscriptiondata && user.subscriptiondata.length > 0 && 
             user.subscriptiondata.some(sub => sub.subscriptionstatus === "active");
    } else {
      return user.subscriptiondata && user.subscriptiondata.length > 0 && 
             user.subscriptiondata.some(sub => sub.Status === "active");
    }
  };

  // UPDATED: Helper function to get customer ID
  const getCustomerId = () => {
    if (user.data[0].type === "Provider") {
      // For providers, get from data[0] or subscriptiondata
      return user.data[0].customerId || 
             (user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].customerId : createCustomerId);
    } else {
      return user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].customerId : createCustomerId;
    }
  };

  // UPDATED: Customeriddb logic
  let Customeriddb;
  if (user.data[0].type === "Provider") {
    Customeriddb = user.data[0].customerId || 
                   (user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].customerId : null);
  } else if (user.subscriptiondata && user.subscriptiondata.length > 0) {
    Customeriddb = user.subscriptiondata[0].customerId;
  }

  console.log("=== SUBSCRIPTION DEBUG INFO ===");
  console.log("User type:", user.data[0].type);
  console.log("User email:", user.data[0].email);
  console.log("Raw user object:", user);
  console.log("Provider subscription data:", user.data[0].type === "Provider" ? user.subscriptiondata : "N/A - Customer");
  console.log("Customer subscription data:", user.data[0].type === "Customer" ? user.subscriptiondata : "N/A - Provider");
  console.log("Active subscription:", getSubscriptionData());
  console.log("Has active subscription:", hasActiveSubscription());
  console.log("Customer ID:", getCustomerId());
  console.log("================================");

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch plans effect
  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      try {
        const headers = {
          Authorization: token,
          "Content-Type": "application/json",
        };
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_STRIPEPRICELIST,
          {},
          { headers }
        );
        
        if (!response.data?.data?.phase_1_admin) {
          throw new Error('Invalid response structure');
        }

        const planData = response.data.data.phase_1_admin;
        const partnerStatus = user.data[0].type === "Provider";
        setIsPartner(partnerStatus);

        const filteredPlans = partnerStatus
          ? planData.filter((plan) => 
              plan.Product_Name.toLowerCase().includes("partner")
            )
          : planData.filter(
              (plan) =>
                !plan.Product_Name.toLowerCase().includes("partner")
            );

        if (filteredPlans.length === 0) {
          console.warn('No plans found after filtering');
        }

        // Add a small delay for smooth UI transition
        setTimeout(() => {
          setPlans(filteredPlans);
          setLoadingPlans(false);
        }, 500);
      } catch (error) {
        console.error("Error fetching plans", error);
        setSnackbar({
          open: true,
          message: 'Failed to fetch subscription plans',
          severity: 'error'
        });
        setLoadingPlans(false);
      }
    };

    if (token) {
      fetchPlans();
    }
  }, [token, user.data[0].type]);

  // UPDATED: Create customer effect
  useEffect(() => {
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    // For providers, check if they need a customer ID
    // For regular customers, create customer if no subscription data
    const needsCustomer = user.data[0].type === "Provider" 
      ? !user.data[0].customerId && (!user.subscriptiondata || user.subscriptiondata.length === 0)
      : (!user.subscriptiondata || user.subscriptiondata.length === 0);

    if (needsCustomer) {
      const data = {
        name: user.data[0].type === "Provider" 
          ? user.data[0].firstName 
          : user.data[0].customer_firstname,
        email: user.data[0].email,
      };

      axios
        .post(process.env.REACT_APP_CREATECUSTOMER, data, { headers: headers })
        .then((response) => {
          const responseData = JSON.parse(response.data.body);
          setCreateCustomerId(responseData.customer.id);
        })
        .catch((error) => {
          console.error("Error in create customer API:", error);
        });
    }
  }, [user.subscriptiondata, user.data[0].type, token]);

  // UPDATED: Fetch card details effect
  useEffect(() => {
    const fetchCardDetails = async () => {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };
  
      const customerId = getCustomerId();
      
      // Get paymentMethodId from subscriptiondata for both providers and customers
      let paymentMethodId = null;
      if (user.subscriptiondata && user.subscriptiondata.length > 0) {
        paymentMethodId = user.subscriptiondata[0]?.paymentMethodId;
      }

      const data = {
        customer: customerId,
        paymentMethodId: paymentMethodId,
      };
  
      try {
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_LISTCARDSDETAILS,
          data,
          { headers: headers }
        );
  
        const responseData = response.data.body.body.card;
        setCardDetails(responseData);
      } catch (error) {
        console.error("Error in fetching card details:", error);
      }
    };
  
    if (Customeriddb || createCustomerId) {
      fetchCardDetails();
    }
  }, [Customeriddb, createCustomerId, token, user.subscriptiondata, user.data[0].type]);
  
  // Effect to ensure fresh subscription data on component mount
  useEffect(() => {
    const loadFreshSubscriptionData = async () => {
      if (!token || !user?.data?.[0]?.email) {
        setSubscriptionDataLoading(false);
        return;
      }
      
      try {
        setSubscriptionDataLoading(true);
        console.log("🔄 Loading fresh subscription data on mount...");
        
        if (user.data[0].type === "Provider") {
          // For providers: Always call getPartner API on mount to get fresh subscription data
          console.log("📞 Calling getPartner API to get fresh provider subscription data...");
          const refreshData = { email: user.data[0].email };
          
          const refreshResponse = await axios.post(
            process.env.REACT_APP_CLIENT_GETPARTNER,
            refreshData,
            { headers: {
              Authorization: token,
              "Content-Type": "application/json",
            }}
          );
          
          if (refreshResponse.data && refreshResponse.data.status === 200) {
            console.log("✅ Fresh provider data loaded on mount:", refreshResponse.data);
            
            // Update Redux state with fresh provider data
            dispatch({
              type: "LOGIN_SUCCESS",
              payload: {
                isLoggedIn: true,
                user: refreshResponse.data,
                userType: "podprovider"
              }
            });
            
            console.log("✅ Provider subscription data refreshed on mount");
          } else {
            console.error("❌ Invalid response from getPartner API on mount");
          }
        } else {
          // For customers: The login should have already fetched fresh data
          console.log("ℹ️ Customer data should already be fresh from login");
        }
        
      } catch (error) {
        console.error("❌ Failed to load fresh subscription data on mount:", error);
      } finally {
        setSubscriptionDataLoading(false);
        console.log("✅ Subscription data loading completed");
      }
    };

    loadFreshSubscriptionData();
  }, [token, user?.data?.[0]?.email, user?.data?.[0]?.type]);

  // Effect to ensure fresh data on component mount (legacy - keeping for customers)
  useEffect(() => {
    const ensureFreshData = async () => {
      if (!token || !user?.data?.[0]?.email || user.data[0].type === "Provider") {
        // Skip for providers as they're handled in the effect above
        return;
      }
      
      try {
        // Only refresh if we haven't refreshed recently (avoid infinite loops)
        const lastRefresh = localStorage.getItem('lastSubscriptionRefresh');
        const now = Date.now();
        
        if (!lastRefresh || (now - parseInt(lastRefresh)) > 30000) { // 30 seconds threshold
          console.log("Ensuring fresh customer subscription data on mount...");
          await refreshUserData();
          localStorage.setItem('lastSubscriptionRefresh', now.toString());
          console.log("✅ Fresh customer data loaded on mount");
        }
      } catch (error) {
        console.error("⚠️ Failed to refresh customer data on mount:", error);
        // Don't show error for this background refresh
      }
    };

    ensureFreshData();
  }, [token, user?.data?.[0]?.email]);

  // Subscription check effect
  useEffect(() => {
    // Only check after subscription data is loaded
    if (!subscriptionDataLoading && !hasActiveSubscription()) {
      setSnackbar({
        open: true,
        message: 'Please choose a subscription plan to access all features',
        severity: 'warning'
      });
    }
  }, [user, subscriptionDataLoading]);

  // Calculate total amount effect
  useEffect(() => {
    const calculateTotalAmount = () => {
      if (currentPlan) {
        const baseAmount = currentPlan.price;
        const amountPerPerson = baseAmount * numberOfPersons;
        const finalAmount = billingCycle === "annually" 
          ? amountPerPerson * 12 * 0.9 // 10% discount for annual
          : amountPerPerson;
  
        setTotalAmount(finalAmount);
      } else {
        setTotalAmount(0);
      }
    };
  
    calculateTotalAmount();
  }, [currentPlan, numberOfPersons, billingCycle]);

  // UPDATED: Fetch invoices effect
  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }
  
    let customerId = null;
    
    if (user.data[0].type === "Provider") {
      // For providers, get customerId from data[0] or subscriptiondata
      customerId = user.data[0].customerId || 
                   (user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].customerId : null);
    } else {
      // For customers, get customerId from subscriptiondata
      if (user && user.subscriptiondata && user.subscriptiondata.length > 0) {
        customerId = user.subscriptiondata[0].customerId;
      }
    }
    
    if (customerId) {
      const body = {
        customerId: customerId,
      };

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      axios.post(process.env.REACT_APP_CLIENT_LISTOFINVOICES, body, { headers })
        .then((res) => {
          const responseData = JSON.parse(res.data.body);
          setGetpoddetails(responseData.body);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error:", err);
          setIsLoading(false);
        });
    } else {
      setErrorMessage(intl.formatMessage({ id: "noInvoices" }));
      setIsLoading(false);
    }
  }, [token, user]);

  // Handle number of persons change
  const handleNumberOfPersonsChange = (event) => {
    const value = Math.max(1, parseInt(event.target.value, 10));
    setNumberOfPersons(value);
  };

  // Handle billing cycle change
  const handleBillingCycleChange = (event) => {
    setBillingCycle(event.target.value);
  };

  // Handle choose plan
  const handleChoosePlan = async (plan) => {
    // Check if user already has an active subscription
    if (hasActiveSubscription()) {
      setSnackbar({
        open: true,
        message: 'You already have an active subscription. Please cancel it before subscribing to a new plan.',
        severity: 'warning'
      });
      return;
    }

    const planAmount = Number(plan.Amount) || 0;
    setCurrentPlan({
      id: plan.Price_ID,
      price: plan.Amount,
      balance: 1,
      type: "Web App",
      Product_Name: plan.Product_Name,
    });

    try {
      setLoadingPlanId(plan.Price_ID); // Set loading for this specific plan
      
      if (planAmount === 0) {
        await createPayment(null, plan);
        setShowSuccessDialog(true);
      } else if (cardDetails) {
        setShowExistingCardDialog(true);
      } else {
        setShowAddCardDialog(true);
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setSnackbar({
        open: true,
        message: 'Failed to process subscription',
        severity: 'error'
      });
    } finally {
      setLoadingPlanId(null); // Clear loading state
    }
  };

  // Handle continue to checkout
  const handleContinue = async () => {
    if (!currentPlan) return;
    setShowAddCardDialog(false);
    setLoading(true);

    const payload = {
      customerId: getCustomerId(),
      paymentMethodId: useExistingCard && user.subscriptiondata && user.subscriptiondata.length > 0 && user.subscriptiondata[0].paymentMethodId != null 
        ? user.subscriptiondata[0].paymentMethodId 
        : paymentID,
      priceId: currentPlan.id,
      quantity: numberOfPersons,
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_TESTSUBSCRIPTIONPAIDPLAN,
        payload
      );

      const responseBody = response.data.body;
      setResponseBody(responseBody);
      
      const totalAmount = responseBody.subscription?.latest_invoice?.subtotal || 0;
      setTotalAmountWithTax(totalAmount);
      setShowTaxDetails(true);
      setShowSecondCard(true);
      
      setSnackbar({
        open: true,
        message: 'Checkout information prepared',
        severity: 'success'
      });
    } catch (error) {
      console.error('Subscription error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to prepare checkout',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: Handle subscribe with proper data refresh
  const handleSubscribe = async () => {
    setLoading(true);
    
    if (!responseBody) return;

    const subscription = responseBody.subscription || {};

    // Use different field names for providers vs customers
    const subData = user.data[0].type === "Provider" ? {
      subscriptionstatus: "active",
      startDate: subscription.current_period_start || "",
      endDate: subscription.current_period_end || "",
      priceID: currentPlan.id,
      subscriptionID: subscription.id || "free_plan_id",
      amount: currentPlan.price,
      CandidateBalance: currentPlan.balance,
      partner_ID: user.data[0].id,
      subId: user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].id : undefined,
      type: currentPlan.type,
      region: user.data[0].region,
      Product_Name: currentPlan.Product_Name,
      partner_code: user.data[0].partner_code,
      customerId: getCustomerId(),
      paymentMethodId: useExistingCard && user.subscriptiondata && user.subscriptiondata.length > 0 && user.subscriptiondata[0].paymentMethodId != null 
        ? user.subscriptiondata[0].paymentMethodId 
        : paymentID,
    } : {
      Status: "active",
      startDate: subscription.current_period_start || "",
      endDate: subscription.current_period_end || "",
      priceID: currentPlan.id,
      subscriptionID: subscription.id || "free_plan_id",
      amount: currentPlan.price,
      CandidateBalance: currentPlan.balance,
      client_ID: user.data[0].customer_id,
      subId: user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].id : undefined,
      type: currentPlan.type,
      region: user.data[0].customer_country,
      Product_Name: currentPlan.Product_Name,
      organizational_code: user.data[0].client_code,
      customerId: getCustomerId(),
      paymentMethodId: useExistingCard && user.subscriptiondata && user.subscriptiondata.length > 0 && user.subscriptiondata[0].paymentMethodId != null 
        ? user.subscriptiondata[0].paymentMethodId 
        : paymentID,
    };

    try {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };
      
      // Use different API endpoints for providers vs customers
      const subscriptionDbApi = user.data[0].type === "Provider" 
        ? process.env.REACT_APP_CLIENT_PARTNERSUBSCRIPTIONCERATEDB
        : process.env.REACT_APP_CLIENT_CREATESUBSCRIPTIONDB;
      
      console.log("Creating subscription via handleSubscribe:", subData);
      const dbResponse = await axios.post(subscriptionDbApi, subData, { headers });
      console.log("✅ Subscription saved to database");

      // Wait for database to process
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Refresh user data using appropriate API with retry logic
      console.log("🔄 Refreshing user data after handleSubscribe...");
      let refreshAttempts = 0;
      const maxRetries = 3;
      let refreshSuccess = false;

      while (refreshAttempts < maxRetries && !refreshSuccess) {
        try {
          refreshAttempts++;
          console.log(`🔄 Refresh attempt ${refreshAttempts}/${maxRetries}...`);
          
          if (user.data[0].type === "Provider") {
            // For providers: Call getPartner API
            const refreshData = { email: user.data[0].email };
            const refreshResponse = await axios.post(
              process.env.REACT_APP_CLIENT_GETPARTNER,
              refreshData,
              { headers }
            );
            
            if (refreshResponse.data && refreshResponse.data.status === 200) {
              dispatch({
                type: "LOGIN_SUCCESS",
                payload: {
                  isLoggedIn: true,
                  user: refreshResponse.data,
                  userType: "podprovider"
                }
              });
              refreshSuccess = true;
              console.log("✅ Provider data refreshed in handleSubscribe");
            } else {
              throw new Error("Invalid getPartner response");
            }
          } else {
            dispatch(login(user.data[0].email, "customer"));
            refreshSuccess = true;
            console.log("✅ Customer data refreshed in handleSubscribe");
          }
          
        } catch (refreshError) {
          console.error(`❌ Refresh attempt ${refreshAttempts} failed:`, refreshError);
          if (refreshAttempts < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (refreshSuccess) {
        console.log("✅ User data refreshed after subscription");
        setSnackbar({
          open: true,
          message: 'Subscription completed and data refreshed successfully!',
          severity: 'success'
        });
      } else {
        console.error("⚠️ Failed to refresh user data, but subscription was successful");
        setSnackbar({
          open: true,
          message: 'Subscription created but failed to refresh data. Please refresh the page.',
          severity: 'warning'
        });
      }
      
      setIsSubscribed(true);
      
      setSnackbar({
        open: true,
        message: 'Subscription completed successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Subscription DB error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to complete subscription',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Create payment
  const createPayment = async (e, plan) => {
    if (e) e.preventDefault();
    setLoading(true);
    
    if (hasActiveSubscription()) {
      setSnackbar({
        open: true,
        message: 'You already have an active plan. Please cancel it before subscribing to a new one.',
        severity: 'warning'
      });
      setLoading(false);
      setLoadingPlanId(null); // Clear plan-specific loading
      return;
    }
    
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
  
      if (currentPlan.price === 0) {
        const data = {
          price_id: plan.Price_ID,
          customerId: getCustomerId(),
        };

        try {
          const response = await axios.post(
            process.env.REACT_APP_TESTCREATESUBSCRIPTION, 
            data, 
            { headers: headers }
          );
      
          const responseBody = JSON.parse(response.data.body);
          const subscriptionBody = JSON.parse(responseBody.subscription.body);
          const Start = subscriptionBody.subscription;
        
          setAPIresponse(Start);
          setPaymentID(null);
          setAddCard(true);
          
          setSnackbar({
            open: true,
            message: 'Free plan subscription created successfully',
            severity: 'success'
          });
        } catch (error) {
          console.error('Error in test-create-subscription API:', error);
          throw error;
        }
      } else {
        const paymentData = {
          number: newCard.number,
          exp_month: newCard.exp_month,
          exp_year: newCard.exp_year,
          cvc: newCard.cvc,
          postal_code: newCard.postal_code,
          name: newCard.name,
          customerId: getCustomerId(),
        };
  
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_CREATEPAYMENTMETHOD, 
          paymentData, 
          { headers }
        );
  
        if (response.data && response.data.result) {
          setPaymentID(response.data.result.id);
          setAddCard(true);
          setIsSuccess(true);
          
          setSnackbar({
            open: true,
            message: 'Payment method added successfully',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Invalid card details. Please try again with valid information.',
            severity: 'error'
          });
          throw new Error("Failed to create payment method.");
        }
      }
    } catch (error) {
      console.error('Error in createPayment:', error);
      setLoadingPlanId(null); // Clear plan-specific loading on error
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle add card
  const handleAddCard = async () => {
    setLoading(true);

    try {
      await createPayment(null, currentPlan);
    } catch (error) {
      console.error("Error adding card:", error);
      setSnackbar({
        open: true,
        message: 'Failed to add card',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setLoadingPlanId(null); // Clear plan-specific loading
      setShowAddCardDialog(false);
    }
  };

  // Handle card input change
  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    setNewCard({ ...newCard, [name]: value });
  };

  // Handle edit card
  const handleEditCard = () => {
    setShowEditCardDialog(true);
  };

  // Handle save card
  const handleSaveCard = () => {
    setCardDetails(newCard);
    setShowEditCardDialog(false);
    
    setSnackbar({
      open: true,
      message: 'Card details updated successfully',
      severity: 'success'
    });
  };

  // UPDATED: Handle confirm subscription with proper loading and API completion
  const handleConfirmSubscription = async () => {
    setConfirmingSubscription(true); // Set confirm button loading
    setLoading(true); // Also set general loading for any other UI elements
    
    try {
      console.log("🔄 Starting subscription confirmation process...");
      
      // Step 1: Create subscription
      await createSubscription();
      console.log("✅ Step 1: Subscription created successfully");
      
      // Step 2: Wait a moment for backend to process
      console.log("⏳ Waiting for backend processing...");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
      
      // Step 3: Force refresh user data to ensure latest state
      console.log("🔄 Step 3: Force refreshing user data...");
      let refreshAttempts = 0;
      const maxRetries = 3;
      let refreshSuccess = false;

      while (refreshAttempts < maxRetries && !refreshSuccess) {
        try {
          refreshAttempts++;
          console.log(`🔄 Refresh attempt ${refreshAttempts}/${maxRetries}...`);
          
          if (user.data[0].type === "Provider") {
            // For providers: Call getPartner API to refresh data
            console.log("📞 Calling getPartner API to refresh provider data...");
            const refreshData = { email: user.data[0].email };
            
            const refreshResponse = await axios.post(
              process.env.REACT_APP_CLIENT_GETPARTNER,
              refreshData,
              { headers: {
                Authorization: token,
                "Content-Type": "application/json",
              }}
            );
            
            if (refreshResponse.data && refreshResponse.data.status === 200) {
              console.log("✅ Provider data refreshed successfully");
              
              // Update Redux state with fresh provider data
              dispatch({
                type: "LOGIN_SUCCESS",
                payload: {
                  isLoggedIn: true,
                  user: refreshResponse.data,
                  userType: "podprovider"
                }
              });
              
              refreshSuccess = true;
            } else {
              throw new Error(`Invalid response from getPartner API`);
            }
          } else {
            // For customers: Use existing login dispatch
            console.log("📞 Refreshing customer data via login dispatch...");
            dispatch(login(user.data[0].email, "customer"));
            refreshSuccess = true;
          }
          
        } catch (refreshError) {
          console.error(`❌ Refresh attempt ${refreshAttempts} failed:`, refreshError);
          
          if (refreshAttempts < maxRetries) {
            // Wait 1 second before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (refreshSuccess) {
        console.log("✅ All operations completed successfully");
        setSnackbar({
          open: true,
          message: 'Subscription confirmed and activated successfully!',
          severity: 'success'
        });
      } else {
        console.log("⚠️ Subscription created but refresh failed");
        setSnackbar({
          open: true,
          message: 'Subscription confirmed but data refresh failed. Please refresh the page.',
          severity: 'warning'
        });
      }
      
      // Close the success dialog
      setShowSuccessDialog(false);
      
      console.log("🏁 Subscription confirmation process completed");
      
    } catch (error) {
      console.error("❌ Error confirming subscription:", error);
      setSnackbar({
        open: true,
        message: 'Failed to confirm subscription. Please try again.',
        severity: 'error'
      });
    } finally {
      setConfirmingSubscription(false); // Clear confirm button loading
      setLoading(false); // Clear general loading
    }
  };

  // Helper function to refresh user data using appropriate API
  const refreshUserData = async (showSuccessMessage = false) => {
    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    if (showSuccessMessage) {
      setRefreshingData(true);
    }

    try {
      if (user.data[0].type === "Provider") {
        // For providers: Call getPartner API
        console.log("Refreshing provider data via getPartner API...");
        const refreshData = { email: user.data[0].email };
        
        const refreshResponse = await axios.post(
          process.env.REACT_APP_CLIENT_GETPARTNER,
          refreshData,
          { headers }
        );
        
        if (refreshResponse.data && refreshResponse.data.status === 200) {
          console.log("✅ Provider data refreshed:", refreshResponse.data);
          
          // Update Redux state with fresh provider data
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: {
              isLoggedIn: true,
              user: refreshResponse.data,
              userType: "podprovider"
            }
          });
          
          if (showSuccessMessage) {
            setSnackbar({
              open: true,
              message: 'Subscription data refreshed successfully',
              severity: 'success'
            });
          }
          
          return true;
        } else {
          throw new Error("Invalid response from getPartner API");
        }
      } else {
        // For customers: Use existing login dispatch (calls get_candidate API)
        console.log("Refreshing customer data via login dispatch...");
        dispatch(login(user.data[0].email, "customer"));
        
        if (showSuccessMessage) {
          setSnackbar({
            open: true,
            message: 'Subscription data refreshed successfully',
            severity: 'success'
          });
        }
        
        return true;
      }
    } catch (error) {
      console.error("❌ Error refreshing user data:", error);
      
      if (showSuccessMessage) {
        setSnackbar({
          open: true,
          message: 'Failed to refresh subscription data',
          severity: 'error'
        });
      }
      
      // Fallback: try basic login dispatch
      const userType = user.data[0].type === "Provider" ? "podprovider" : "customer";
      dispatch(login(user.data[0].email, userType));
      
      throw error;
    } finally {
      if (showSuccessMessage) {
        setRefreshingData(false);
      }
    }
  };

  // UPDATED: Handle delete subscription with proper data refresh
  const deleteSubscription = async () => {
    setLoading(true);
    
    let subscriptionID;
    if (user.data[0].type === "Provider") {
      // For providers, get the active subscription ID from subscriptiondata
      const activeSubscription = user.subscriptiondata?.find(sub => sub.subscriptionstatus === "active");
      subscriptionID = activeSubscription ? activeSubscription.subscriptionID : null;
    } else {
      // For customers, get the active subscription ID
      const activeSubscription = user.subscriptiondata?.find(sub => sub.Status === "active");
      subscriptionID = activeSubscription ? activeSubscription.subscriptionID : null;
    }
    
    if (!subscriptionID) {
      setSnackbar({
        open: true,
        message: 'No active subscription found to cancel',
        severity: 'error'
      });
      setLoading(false);
      return;
    }
    
    try {
      console.log("🔄 Starting subscription cancellation process...");
      console.log("Canceling subscription:", subscriptionID);
      
      // Step 1: Cancel subscription in database
      const cancelData = { subId: subscriptionID };
      await axios.delete(process.env.REACT_APP_CLIENT_CANCELSUBSCRIPTIONDB, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        data: cancelData,
      });

      console.log("✅ Step 1: Subscription canceled in database");

      // Step 2: Update subscription status
    if (user.data[0].type === "Provider") {
  // For Provider users - use partner endpoint
  const updateData = {
    partner_ID: user.data[0].id
  };

  await axios.post(process.env.REACT_APP_CLIENT_CANCELSUBSCRIPTIONDBPARTNER, updateData, {
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
  });
} else {
  // For non-Provider users - use original endpoint
  const updateData = {
    client_Id: user.data[0].customer_id,
  };

  await axios.put(process.env.REACT_APP_CLIENT_CANCELSUBSCRIPTIONDBSECOND, updateData, {
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
  });
}

      console.log("✅ Step 2: Subscription status updated");

      // Step 3: CRITICAL - Refresh user data using appropriate API with retry logic
      console.log("🔄 Step 3: Refreshing user data after cancellation...");
      
      let refreshAttempts = 0;
      const maxRetries = 3;
      let refreshSuccess = false;

      while (refreshAttempts < maxRetries && !refreshSuccess) {
        try {
          refreshAttempts++;
          console.log(`🔄 Refresh attempt ${refreshAttempts}/${maxRetries}...`);
          
          if (user.data[0].type === "Provider") {
            // For providers: Call getPartner API to refresh data
            console.log("📞 Calling getPartner API to refresh provider data...");
            const refreshData = { email: user.data[0].email };
            
            const refreshResponse = await axios.post(
              process.env.REACT_APP_CLIENT_GETPARTNER,
              refreshData,
              { headers: {
                Authorization: token,
                "Content-Type": "application/json",
              }}
            );
            
            if (refreshResponse.data && refreshResponse.data.status === 200) {
              console.log("✅ Provider data refreshed successfully:", refreshResponse.data);
              
              // Update Redux state with fresh provider data
              dispatch({
                type: "LOGIN_SUCCESS",
                payload: {
                  isLoggedIn: true,
                  user: refreshResponse.data,
                  userType: "podprovider"
                }
              });
              
              refreshSuccess = true;
              console.log("✅ Redux state updated with fresh provider data");
            } else {
              throw new Error(`Invalid response from getPartner API: ${JSON.stringify(refreshResponse.data)}`);
            }
          } else {
            // For customers: Use existing login dispatch (calls get_candidate API)
            console.log("📞 Refreshing customer data via login dispatch...");
            dispatch(login(user.data[0].email, "customer"));
            refreshSuccess = true;
            console.log("✅ Customer data refresh dispatched");
          }
          
        } catch (refreshError) {
          console.error(`❌ Refresh attempt ${refreshAttempts} failed:`, refreshError);
          
          if (refreshAttempts === maxRetries) {
            console.error("❌ All refresh attempts failed");
            throw refreshError;
          } else {
            // Wait 1 second before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (refreshSuccess) {
        console.log("✅ User data refresh completed successfully");
        setSnackbar({
          open: true,
          message: 'Subscription canceled and data refreshed successfully!',
          severity: 'success'
        });
      }
      
    } catch (error) {
      console.error("❌ Error during subscription cancellation:", error);
      
      // Still try a fallback refresh even if there was an error
      try {
        console.log("🔄 Attempting fallback refresh...");
        const userType = user.data[0].type === "Provider" ? "podprovider" : "customer";
        dispatch(login(user.data[0].email, userType));
        
        setSnackbar({
          open: true,
          message: 'Subscription canceled, but data refresh may be delayed. Please refresh the page if needed.',
          severity: 'warning'
        });
      } catch (fallbackError) {
        console.error("❌ Fallback refresh also failed:", fallbackError);
        setSnackbar({
          open: true,
          message: 'Subscription canceled, but failed to refresh data. Please refresh the page.',
          severity: 'warning'
        });
      }
    } finally {
      setLoading(false);
      console.log("🏁 Subscription cancellation process completed");
    }
  };

  // Handle invoice download
  const handleInvoiceDownload = (index, event) => {
    event.preventDefault();
    setDownloadingIndex(index);

    // Create a temporary link and trigger the download
    const link = document.createElement('a');
    link.href = getpoddetails[index].invoice_pdf;
    link.download = true;
    link.click();

    // Set a timeout to clear the downloading state
    setTimeout(() => {
      setDownloadingIndex(null);
    }, 1000);
  };

  // UPDATED: Create subscription with proper API refresh for both user types
  const createSubscription = async () => {
    try {
      console.log("Creating subscription for:", currentPlan);

      // Determine the correct paymentMethodId for both Stripe APIs and database
      let paymentMethodId = null;
      
      if (currentPlan.price === 0) {
        paymentMethodId = null;
      } else {
        paymentMethodId = useExistingCard && 
                        user.subscriptiondata && 
                        user.subscriptiondata.length > 0 && 
                        user.subscriptiondata[0].paymentMethodId != null 
          ? user.subscriptiondata[0].paymentMethodId 
          : paymentID;
      }

      const data = {
        paymentMethodId: paymentMethodId,
        customerId: getCustomerId(),
        items: [{ price: currentPlan.id }],
        subId: user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].subscriptionID : undefined,
      };

      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      let response;

      if (currentPlan.price !== 0) {
        console.log("Creating paid subscription");
        const subscriptionApi = process.env.REACT_APP_CLIENT_CREATESUBSCRIPTION;
        response = await axios.post(subscriptionApi, data, { headers });
      } else {
        console.log("Free plan detected, using APIresponse:", APIresponse);
        response = APIresponse;
      }

      // Database payload - different for providers and customers
      let subData;
      let subscriptionDbApi;
      
      if (user.data[0].type === "Provider") {
        // Provider-specific database structure
        subData = {
          subscriptionstatus: "active",
          startDate: response.current_period_start || APIresponse?.current_period_start,
          endDate: response.current_period_end || APIresponse?.current_period_end,
          priceID: currentPlan.id,
          subscriptionID: response.id || APIresponse?.id || "free_plan_id",
          amount: currentPlan.price,
          CandidateBalance: currentPlan.balance,
          partner_ID: user.data[0].id,
          subId: user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].id : undefined,
          type: currentPlan.type,
          region: user.data[0].region,
          Product_Name: currentPlan.Product_Name,
          partner_code: user.data[0].partner_code,
          customerId: getCustomerId(),
          paymentMethodId: paymentMethodId,
        };
        
        subscriptionDbApi = process.env.REACT_APP_CLIENT_PARTNERSUBSCRIPTIONCERATEDB;
      } else {
        // Customer database structure
        subData = {
          Status: "active",
          startDate: response.current_period_start || APIresponse?.current_period_start,
          endDate: response.current_period_end || APIresponse?.current_period_end,
          priceID: currentPlan.id,
          subscriptionID: response.id || APIresponse?.id || "free_plan_id",
          amount: currentPlan.price,
          CandidateBalance: currentPlan.balance,
          client_ID: user.data[0].customer_id,
          subId: user.subscriptiondata && user.subscriptiondata.length > 0 ? user.subscriptiondata[0].id : undefined,
          type: currentPlan.type,
          region: user.data[0].customer_country,
          Product_Name: currentPlan.Product_Name,
          organizational_code: user.data[0].client_code,
          customerId: getCustomerId(),
          paymentMethodId: paymentMethodId,
        };
        
        subscriptionDbApi = process.env.REACT_APP_CLIENT_CREATESUBSCRIPTIONDB;
      }

      console.log("Saving subscription to database:", subData);
      
      const dbResponse = await axios.post(subscriptionDbApi, subData, { headers });
      console.log("Database response:", dbResponse.data);

      // CRITICAL: After successful DB save, refresh user data using the appropriate API
      console.log("🔄 Refreshing user data after subscription creation...");
      
      // Wait a moment for the database to process the subscription
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      try {
        if (user.data[0].type === "Provider") {
          // For providers: Call getPartner API to refresh data
          console.log("📞 Calling getPartner API to refresh provider data...");
          const refreshData = { email: user.data[0].email };
          
          const refreshResponse = await axios.post(
            process.env.REACT_APP_CLIENT_GETPARTNER,
            refreshData,
            { headers }
          );
          
          if (refreshResponse.data && refreshResponse.data.status === 200) {
            console.log("✅ Provider data refreshed successfully:", refreshResponse.data);
            
            // Update Redux state with fresh provider data including new subscription
            dispatch({
              type: "LOGIN_SUCCESS",
              payload: {
                isLoggedIn: true,
                user: refreshResponse.data,
                userType: "podprovider"
              }
            });
            
            console.log("✅ Redux state updated with fresh provider data");
          } else {
            throw new Error("Invalid response from getPartner API");
          }
        } else {
          // For customers: Call existing login/refresh logic (get_candidate API)
          console.log("📞 Refreshing customer data using existing login dispatch...");
          dispatch(login(user.data[0].email, "customer"));
        }
        
        console.log("✅ User data refresh completed successfully");
      } catch (refreshError) {
        console.error("❌ Error refreshing user data:", refreshError);
        
        // Show error but don't fail the subscription
        setSnackbar({
          open: true,
          message: 'Subscription created but failed to refresh data. Please refresh the page.',
          severity: 'warning'
        });
      }

      setStep("Subscribed");
      
      setSnackbar({
        open: true,
        message: 'Subscription created successfully!',
        severity: 'success'
      });

      console.log("✅ Subscription creation completed successfully");
    } catch (err) {
      console.error("❌ Error during subscription creation:", err.response ? err.response.data : err.message);
      setSnackbar({
        open: true,
        message: 'Failed to create subscription',
        severity: 'error'
      });
      throw err;
    }
  };
  
  // Close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const parseInvoiceDate = (dateStr) => {
    try {
      // Handle the format "2025-05-12T07:29:43 AM"
      if (typeof dateStr === 'string') {
        // Replace the 'T' with a space for better parsing
        const cleanedDate = dateStr.replace('T', ' ');
        const date = new Date(cleanedDate);
        
        // Check if date is valid
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }
      
      // Fallback: try parsing as-is
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      
      return 'Invalid Date';
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'Invalid Date';
    }
  };

  // Render skeleton card for loading state
  const renderSkeletonCard = () => (
    <StyledCard>
      <CardHeader>
        <Skeleton animation="wave" height={40} width="60%" />
      </CardHeader>
      <CardBody>
        <Skeleton animation="wave" height={60} style={{ marginBottom: 16 }} />
        <Skeleton animation="wave" height={20} style={{ marginBottom: 8 }} />
        <Skeleton animation="wave" height={20} style={{ marginBottom: 8 }} />
        <Skeleton animation="wave" height={20} style={{ marginBottom: 8 }} />
        <Skeleton animation="wave" height={20} style={{ marginBottom: 8 }} />
      </CardBody>
      <CardFooter>
        <Skeleton animation="wave" height={40} width="100%" />
      </CardFooter>
    </StyledCard>
  );

  // UPDATED: Check if current plan matches a plan (with loading state consideration)
  const isCurrentPlan = (plan) => {
    // Don't show as current plan if subscription data is still loading
    if (subscriptionDataLoading) {
      return false;
    }
    
    if (user.data[0].type === "Provider") {
      // For providers, check against active subscription in subscriptiondata
      const activeSubscription = user.subscriptiondata?.find(sub => sub.subscriptionstatus === "active");
      return activeSubscription && activeSubscription.priceID === plan.Price_ID;
    } else {
      // For customers, check against active subscription in subscriptiondata
      const activeSubscription = user.subscriptiondata?.find(sub => sub.Status === "active");
      return activeSubscription && activeSubscription.priceID === plan.Price_ID;
    }
  };

  // UPDATED: Render current plan tab (with loading state)
  const renderCurrentPlanTab = () => {
    if (subscriptionDataLoading) {
      return (
        <Box p={3} textAlign="center">
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Loading subscription data...
          </Typography>
        </Box>
      );
    }
    
    let subscriptionData = null;
    
    if (user.data[0].type === "Provider") {
      // For providers, get active subscription from subscriptiondata
      subscriptionData = user.subscriptiondata?.find(sub => sub.subscriptionstatus === "active");
    } else {
      // For customers, get active subscription from subscriptiondata
      subscriptionData = user.subscriptiondata?.find(sub => sub.Status === "active");
    }
    
    if (!subscriptionData) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="text.secondary">No active subscription found</Typography>
        </Box>
      );
    }

    return (
      <Box mt={4}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: theme => theme.palette.mode === 'dark' ? '#fff' : 'inherit',
          fontWeight: 600,
          fontSize: 20,
          marginBottom: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {intl.formatMessage({ id: "currentPlanDetails" })}
          <Button
            variant="outlined"
            size="small"
            onClick={() => refreshUserData(true)}
            disabled={refreshingData || subscriptionDataLoading}
            startIcon={refreshingData ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{ 
              minWidth: 'auto',
              padding: '4px 8px',
              fontSize: '0.75rem'
            }}
          >
            {refreshingData ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Typography>
        <CurrentPlanWrapper>
          <CurrentPlanHeader>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip 
                label="active" 
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontWeight: 500,
                  borderRadius: 16,
                  height: 24,
                  fontSize: 12
                }}
                size="small"
              />
              <Typography variant="subtitle1" fontWeight={500}>
                ${subscriptionData.amount || 0}/month
              </Typography>
            </Box>
          </CurrentPlanHeader>
          
          <CurrentPlanContent>
            <Grid container spacing={5}>
              {subscriptionData.startDate && (
                <Grid item xs={12} md={6}>
                  <CurrentPlanLabel>
                    {intl.formatMessage({ id: "startDate" })}
                  </CurrentPlanLabel>
                  <CurrentPlanValue sx={{ fontSize: '1.1rem' }}>
                    {new Date(subscriptionData.startDate * 1000).toLocaleDateString()}
                  </CurrentPlanValue>
                </Grid>
              )}
              {subscriptionData.endDate && (
                <Grid item xs={12} md={6}>
                  <CurrentPlanLabel>
                    {intl.formatMessage({ id: "renewalDate" })}
                  </CurrentPlanLabel>
                  <CurrentPlanValue sx={{ fontSize: '1.1rem' }}>
                    {new Date(subscriptionData.endDate * 1000).toLocaleDateString()}
                  </CurrentPlanValue>
                </Grid>
              )}
              <Grid item xs={12}>
                <CurrentPlanLabel>
                  {intl.formatMessage({ id: "subscriptionId" })}
                </CurrentPlanLabel>
                <CurrentPlanValue sx={{ wordBreak: 'break-all' }}>
                  {subscriptionData.subscriptionID}
                </CurrentPlanValue>
              </Grid>
            </Grid>
            
            <Box mt={4} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={deleteSubscription}
                disabled={loading || subscriptionDataLoading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{ 
                  backgroundColor: '#f44336',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#d32f2f'
                  },
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  padding: '8px 16px',
                  borderRadius: '4px'
                }}
              >
                {intl.formatMessage({ id: "cancelSubscription" })}
              </Button>
            </Box>
          </CurrentPlanContent>
        </CurrentPlanWrapper>
      </Box>
    );
  };

  // Check if should show current plan tab
  const showCurrentPlanTab = hasActiveSubscription();

  // Render UI - Main component
  return (
    <PageContainer>
      <PageHeader>
        <MainTitle>
          {intl.formatMessage({ id: "subscriptionManagement" })}
          {(loading || refreshingData || confirmingSubscription || subscriptionDataLoading) && (
            <CircularProgress size={24} sx={{ ml: 2 }} />
          )}
        </MainTitle>
        <SubTitle>
          {intl.formatMessage({ id: "choosePlanPrompt" })}
          {(loading || refreshingData || confirmingSubscription || subscriptionDataLoading) && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ 
                color: 'primary.main', 
                fontWeight: 500, 
                mt: 1,
                fontSize: '0.875rem'
              }}
            >
              {subscriptionDataLoading ? 'Loading subscription data...' :
               confirmingSubscription ? 'Confirming subscription...' : 
               refreshingData ? 'Refreshing data...' : 
               'Processing...'}
            </Typography>
          )}
        </SubTitle>
      </PageHeader>

      {/* Main Content */}
      {!isSuccess ? (
        <ContentGrid>
          <PlansSection>
            <TabsContainer>
              <StyledTabs 
                value={activeTab} 
                onChange={handleTabChange}
                centered
              >
                <StyledTab label="Available Plans" />
                {showCurrentPlanTab && (
                  <StyledTab label="Current Plan" />
                )}
              </StyledTabs>
            </TabsContainer>
   
            {activeTab === 0 && (
              <PlanCards>
                {loadingPlans ? (
                  // Skeleton cards while loading
                  Array(3).fill().map((_, index) => (
                    <Fade in key={index} style={{ transitionDelay: `${index * 150}ms` }}>
                      {renderSkeletonCard()}
                    </Fade>
                  ))
                ) : plans && plans.length > 0 ? (
                  plans
                    // Fixed filter logic: 
                    // For partners: show only partner plans
                    // For customers: show customer plans (including free plan) but exclude partner plans
                    .filter(plan => {
                      if (isPartner) {
                        // For partners, show only partner plans
                        return plan.Product_Name.toLowerCase().includes("partner");
                      } else {
                        // For customers, exclude partner plans but show everything else (including customer free plan)
                        return !plan.Product_Name.toLowerCase().includes("partner");
                      }
                    })
                    .map((plan, index) => (
                      <Fade in key={plan.Price_ID} style={{ transitionDelay: `${index * 150}ms` }}>
                        <StyledCard 
                          featured={isCurrentPlan(plan)}
                        >
                          {isCurrentPlan(plan) && (
                            <PlanBadge>Current Plan</PlanBadge>
                          )}
                          
                          <CardHeader bgcolor={plan.Amount === 0 
                            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))' 
                            : 'var(--artemy-accent-gradient)'
                          }>
                            <Typography 
                              variant="h5" 
                              fontWeight={600} 
                              gutterBottom
                              sx={{ color: 'var(--artemy-text-primary)' }}
                            >
                              {plan.Product_Name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ color: 'var(--artemy-text-secondary)' }}
                            >
                              {plan.Desc}
                            </Typography>
                          </CardHeader>
                          
                          <CardBody>
                            <PriceTag>
                              ${Number(plan.Amount || 0).toFixed(2)}
                              <PriceUnit sx={{ color: 'var(--artemy-text-secondary)' }}>
                                /month
                              </PriceUnit>
                            </PriceTag>
                            
                            <FeaturesList>
                              {plan.features && plan.features.split(',').map((feature, idx) => (
                                <FeatureItem key={idx}>
                                  <CheckCircleIcon 
                                    sx={{ 
                                      color: 'var(--artemy-accent)', 
                                      fontSize: 20, 
                                      marginRight: 1 
                                    }} 
                                  />
                                  <Typography 
                                    variant="body2"
                                    sx={{ color: 'var(--artemy-text-primary)' }}
                                  >
                                    {feature.trim()}
                                  </Typography>
                                </FeatureItem>
                              ))}
                            </FeaturesList>
                          </CardBody>
                          
                          <CardFooter>
                            {subscriptionDataLoading ? (
                              // Show loading state while subscription data is being fetched
                              <ActionButton
                                variant="outlined"
                                disabled={true}
                                startIcon={<CircularProgress size={20} />}
                                sx={{
                                  borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                                  color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                }}
                              >
                                Loading...
                              </ActionButton>
                            ) : isCurrentPlan(plan) ? (
                              <ActionButton
                                variant="outlined"
                                color="error"
                                onClick={deleteSubscription}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : null}
                                sx={{
                                  borderColor: theme => theme.palette.mode === 'dark' ? '#f44336' : undefined,
                                  color: theme => theme.palette.mode === 'dark' ? '#f44336' : undefined,
                                }}
                              >
                                {intl.formatMessage({ id: "cancelSubscription" })}
                              </ActionButton>
                            ) : (
                              <ActionButton
                                variant="contained"
                                onClick={() => handleChoosePlan(plan)}
                                disabled={loadingPlanId === plan.Price_ID || subscriptionDataLoading}
                                startIcon={loadingPlanId === plan.Price_ID ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
                                sx={{
                                  background: 'var(--artemy-accent-gradient)',
                                  color: '#fff !important',
                                  boxShadow: '0 4px 15px rgba(61, 127, 255, 0.2)',
                                  '&:hover': {
                                    boxShadow: '0 6px 20px rgba(61, 127, 255, 0.3)',
                                  }
                                }}
                              >
                                {loadingPlanId === plan.Price_ID ? 'Processing...' : intl.formatMessage({ id: "choosePlan" })}
                              </ActionButton>
                            )}
                          </CardFooter>
                        </StyledCard>
                      </Fade>
                    ))
                ) : (
                  <StyledCard>
                    <CardBody>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: 3,
                        textAlign: 'center'
                      }}>
                        <ErrorOutlineIcon 
                          sx={{ 
                            fontSize: 40,
                            color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'text.secondary',
                            marginBottom: 2
                          }}
                        />
                        <Typography 
                          variant="h6" 
                          sx={{ color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
                          gutterBottom
                        >
                          {intl.formatMessage({ id: "noPlansAvailable" })}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'text.secondary' }}
                        >
                          {intl.formatMessage({ id: "noPlansHelpText" })}
                        </Typography>
                      </Box>
                    </CardBody>
                  </StyledCard>
                )}
              </PlanCards>
            )}

            {activeTab === 1 && showCurrentPlanTab && renderCurrentPlanTab()}

            {cardDetails && (
              <CreditCardBox>
                <Box sx={{ padding: 3, position: 'relative' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>{intl.formatMessage({ id: "paymentMethod" })}</Typography>
                  
                  <Box position="absolute" top={16} right={16}>
                    <IconButton onClick={() => setCardNumberMasked(!cardNumberMasked)} size="small">
                      {cardNumberMasked ? 
                        <VisibilityIcon fontSize="small" sx={{ color: 'white' }} /> : 
                        <VisibilityOffIcon fontSize="small" sx={{ color: 'white' }} />
                      }
                    </IconButton>
                  </Box>
                  
                  <Box display="flex" flexDirection="column" gap={2} mt={2}>
                    <CardChip />
                    
                    <Typography variant="h5" letterSpacing={2}>
                      {cardNumberMasked ? 
                        `**** **** **** ${cardDetails.last4}` : 
                        `4242 4242 4242 ${cardDetails.last4}`
                      }
                    </Typography>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                          {intl.formatMessage({ id: "cardHolder" })}
                        </Typography>
                        <Typography variant="body2">
                          {cardDetails.name || (user.data[0].type === "Provider" ? user.data[0].firstName : user.data[0].customer_firstname)}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                          {intl.formatMessage({ id: "expires" })}
                        </Typography>
                        <Typography variant="body2">
                          {cardDetails.exp_month}/{cardDetails.exp_year}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                          {intl.formatMessage({ id: "brand" })}
                        </Typography>
                        <Typography variant="body2" textTransform="capitalize">
                          {cardDetails.brand}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CreditCardBox>
            )}
          </PlansSection>
          
          <SidebarSection>
            <InvoiceList>
              <InvoiceHeader>
                <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" sx={{ color: 'var(--artemy-text-primary)' }}>
                  <ReceiptIcon sx={{ marginRight: 1, color: 'var(--artemy-accent)' }} />
                 {intl.formatMessage({ id: "billingHistory" })}
                </Typography>
              </InvoiceHeader>
              
              {isLoading ? (
                // Skeleton loading for invoices
                Array(4).fill().map((_, index) => (
                  <InvoiceItem key={index}>
                    <Box width="100%">
                      <Skeleton animation="wave" height={24} width="40%" style={{ marginBottom: 8 }} />
                      <Skeleton animation="wave" height={16} width="30%" />
                    </Box>
                    <Skeleton animation="wave" height={24} width={80} />
                  </InvoiceItem>
                ))
              ) : errorMessage ? (
                <Box p={3} textAlign="center">
                  <Typography color="text.secondary">{errorMessage}</Typography>
                </Box>
              ) : !Array.isArray(getpoddetails) || getpoddetails.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography color="text.secondary">{intl.formatMessage({ id: "noInvoiceHistory" })}</Typography>
                </Box>
              ) : (
               getpoddetails.map((invoice, index) => (
                  <InvoiceItem key={index}>
                    <Box>
                      <Typography variant="body1" fontWeight={500} sx={{ color: 'var(--artemy-text-primary)' }}>
                        {parseInvoiceDate(invoice.created)}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'var(--artemy-text-secondary)',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Box 
                          component="span" 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: invoice.status === 'paid' ? '#10B981' : '#F59E0B',
                            display: 'inline-block',
                            marginRight: 1
                          }} 
                        />
                        {invoice.status}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" fontWeight={600} mr={2} sx={{ color: 'var(--artemy-text-primary)' }}>
                        ${(invoice.amount_paid / 100).toFixed(2)}
                      </Typography>
                      
                      <Tooltip title="Download Invoice">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={(event) => handleInvoiceDownload(index, event)}
                          disabled={downloadingIndex === index}
                        >
                          {downloadingIndex === index ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon sx={{ color: 'var(--artemy-accent)' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InvoiceItem>
                ))
              )}
            </InvoiceList>
            
            <Paper sx={{ 
              padding: 3, 
              borderRadius: '24px',
              bgcolor: 'var(--artemy-bg-card)',
              border: '1px solid var(--artemy-border)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }}>
              <Typography variant="h6" fontWeight={600} gutterBottom display="flex" alignItems="center" sx={{ color: 'var(--artemy-text-primary)' }}>
                <HelpOutlineIcon sx={{ marginRight: 1, color: 'var(--artemy-accent)' }} />
                {intl.formatMessage({ id: "needHelp" })}
              </Typography>
              <Typography variant="body2" paragraph sx={{ color: 'var(--artemy-text-secondary)' }}>
                {intl.formatMessage({ id: "supportText" })}
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ 
                  color: 'var(--artemy-accent)',
                  borderColor: 'var(--artemy-accent)',
                  '&:hover': {
                    borderColor: 'var(--artemy-accent-bright)',
                    bgcolor: 'rgba(var(--artemy-accent-rgb), 0.1)'
                  }
                }}
                href="mailto:support@example.com"
              >
                {intl.formatMessage({ id: "contactSupport" })}
              </Button>
            </Paper>
          </SidebarSection>
        </ContentGrid>
      ) : !isSubscribed ? (
        <Fade in>
          <CheckoutContainer>
            <Typography variant="h5" fontWeight={600} gutterBottom align="center">
            {intl.formatMessage({ id: "completeSubscription" })}
            </Typography>
            
            <Divider sx={{ my: 3 }} />
            
            <CheckoutStep>
              <StepTitle>
                <ShoppingCartIcon /> {intl.formatMessage({ id: "planDetails" })}
              </StepTitle>
              
              <Paper sx={{ padding: 3, borderRadius: 2, backgroundColor: '#f8fafc' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{intl.formatMessage({ id: "planName" })}</Typography>
                    <Typography variant="body1" fontWeight={500}>{currentPlan.Product_Name}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{intl.formatMessage({ id: "basePrice" })}</Typography>
                    <Typography variant="body1" fontWeight={500}>${Number(currentPlan.price).toFixed(2)}/month</Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{intl.formatMessage({ id: "numberOfUsers" })}</Typography>
                    <TextField
                      type="number"
                      value={numberOfPersons}
                      onChange={handleNumberOfPersonsChange}
                      inputProps={{ min: 1 }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{intl.formatMessage({ id: "billingCycle" })}</Typography>
                    <RadioGroup
                      value={billingCycle}
                      onChange={handleBillingCycleChange}
                      row
                    >
                      <FormControlLabel value="monthly" control={<Radio color="primary" />} label="Monthly" />
                      <FormControlLabel value="annually" control={<Radio color="primary" />} label="Annually (10% off)" />
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Paper>
            </CheckoutStep>
            
            {cardDetails && (
              <CheckoutStep>
                <StepTitle>
                  <AccountBalanceWalletIcon />{intl.formatMessage({ id: "paymentMethod" })}
                </StepTitle>
                
                <Paper sx={{ padding: 3, borderRadius: 2, backgroundColor: '#f8fafc' }}>
                  <Box display="flex" alignItems="center">
                    <CreditCardIcon sx={{ marginRight: 2, color: '#0D47A1' }} />
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        **** **** **** {cardDetails.last4}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage({ id: "expires" })}
 {cardDetails.exp_month}/{cardDetails.exp_year}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </CheckoutStep>
            )}
            
            <CheckoutSummary>
              <Typography variant="h6" fontWeight={600} gutterBottom>
{intl.formatMessage({ id: "orderSummary" })}
              </Typography>
              
              <SummaryRow>
                <Typography variant="body2">{intl.formatMessage({ id: "basePrice" })}</Typography>
                <Typography variant="body2">${Number(currentPlan.price).toFixed(2)}</Typography>
              </SummaryRow>
              
              <SummaryRow>
                <Typography variant="body2">Users</Typography>
                <Typography variant="body2">x {numberOfPersons}</Typography>
              </SummaryRow>
              
              {billingCycle === "annually" && (
                <SummaryRow>
                  <Typography variant="body2">{intl.formatMessage({ id: "annualDiscount" })}</Typography>
                  <Typography variant="body2" color="success.main">-10%</Typography>
                </SummaryRow>
              )}
              
              <SummaryRow>
                <Typography variant="body2">{intl.formatMessage({ id: "billingPeriod" })}</Typography>
                <Typography variant="body2">{billingCycle === "annually" ? "Annual" : "Monthly"}</Typography>
              </SummaryRow>
              
              <Divider sx={{ my: 2 }} />
              
              <SummaryRow isBold>
                <Typography variant="subtitle1" fontWeight={600}>Total</Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  ${totalAmount.toFixed(2)} {billingCycle === "annually" ? "/year" : "/month"}
                </Typography>
              </SummaryRow>
              
              {showTaxDetails && (
                <>
                  <SummaryRow>
                    <Typography variant="body2">{intl.formatMessage({ id: "tax" })}</Typography>
                    <Typography variant="body2">
                      ${(totalAmountWithTax - totalAmount).toFixed(2)}
                    </Typography>
                  </SummaryRow>
                  
                  <SummaryRow isBold>
                    <Typography variant="subtitle1" fontWeight={600}>{intl.formatMessage({ id: "totalWithTax" })}</Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      ${totalAmountWithTax.toFixed(2)} {billingCycle === intl.formatMessage({ id: "annually" }) ? "/year" : "/month"}
                    </Typography>
                  </SummaryRow>
                </>
              )}
            </CheckoutSummary>
            
            <CheckoutActions>
              {!showTaxDetails ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleContinue}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {intl.formatMessage({ id: "continue" })}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSubscribe}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <LocalAtmIcon />}
                >
                 {intl.formatMessage({ id: "completeSubscription" })}
                </Button>
              )}
              
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => setIsSuccess(false)}
              >
        {intl.formatMessage({ id: "back" })}
              </Button>
            </CheckoutActions>
          </CheckoutContainer>
        </Fade>
      ) : (
        <Fade in>
          <Box textAlign="center" my={8}>
            <Box sx={{ 
              width: 100, 
              height: 100, 
              borderRadius: '50%', 
              backgroundColor: '#d1fae5', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto',
              marginBottom: 4
            }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#10B981' }} />
            </Box>
            
            <Typography variant="h4" gutterBottom>
            {intl.formatMessage({ id: "subscriptionSuccessful" })}
            </Typography>
            
            <Typography variant="body1" paragraph>
             {intl.formatMessage({ id: "thankYouSubscribing" })} {currentPlan.Product_Name}.
            {intl.formatMessage({ id: "subscriptionActivated" })}
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
            {intl.formatMessage({ id: "goToDashboard" })}
            </Button>
          </Box>
        </Fade>
      )}

      {/* Dialogs */}
      <Dialog
        open={showAddCardDialog}
        onClose={() => {
          setShowAddCardDialog(false);
          setLoadingPlanId(null); // Clear plan-specific loading when dialog closes
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: "addPaymentMethod" })}</DialogTitle>
        <DialogContent>
          <DialogForm>
            <TextField
              label={intl.formatMessage({ id: "cardNumber" })}
              name="number"
              value={newCard.number}
              onChange={handleCardInputChange}
              fullWidth
              placeholder="1234 5678 9012 3456"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCardIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              label={intl.formatMessage({ id: "cardholderName" })}
              name="name"
              value={newCard.name}
              onChange={handleCardInputChange}
              fullWidth
              placeholder="John Doe"
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={intl.formatMessage({ id: "expiryMonth" })}
                  name="exp_month"
                  value={newCard.exp_month}
                  onChange={handleCardInputChange}
                  fullWidth
                  placeholder="MM"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label={intl.formatMessage({ id: "expiryYear" })}
                  name="exp_year"
                  value={newCard.exp_year}
                  onChange={handleCardInputChange}
                  fullWidth
                  placeholder="YYYY"
                />
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="CVC"
                  name="cvc"
                  value={newCard.cvc}
                  onChange={handleCardInputChange}
                  fullWidth
                  placeholder="123"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label={intl.formatMessage({ id: "postalCode" })}
                  name="postal_code"
                  value={newCard.postal_code}
                  onChange={handleCardInputChange}
                  fullWidth
                  placeholder="12345"
                />
              </Grid>
            </Grid>
          </DialogForm>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowAddCardDialog(false);
            setLoadingPlanId(null); // Clear plan-specific loading
          }} color="inherit">
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button 
            onClick={handleAddCard} 
            color="primary" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
     {intl.formatMessage({ id: "addCard" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showExistingCardDialog}
        onClose={() => {
          setShowExistingCardDialog(false);
          setLoadingPlanId(null); // Clear plan-specific loading when dialog closes
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: "paymentChoice" })}</DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Typography variant="body1" paragraph>
             {intl.formatMessage({ id: "existingCardText" })}{cardDetails?.last4}.{intl.formatMessage({ id: "useExistingCardPrompt" })}
            </Typography>
            
            <Box display="flex" alignItems="center" p={2} border="1px solid #e0e7ff" borderRadius={2} mb={2}>
              <CreditCardIcon sx={{ marginRight: 2, color: '#0D47A1' }} />
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  **** **** **** {cardDetails?.last4}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: "expires" })} {cardDetails?.exp_month}/{cardDetails?.exp_year}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsSuccess(true);
              setShowExistingCardDialog(false);
              setUseExistingCard(true);
              setLoadingPlanId(null); // Clear plan-specific loading
            }}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
       {intl.formatMessage({ id: "useExistingCard" })}
          </Button>
          <Button
            onClick={() => {
              setShowAddCardDialog(true);
              setShowExistingCardDialog(false);
              setUseExistingCard(false);
              // Keep loadingPlanId as it transitions to add card dialog
            }}
            color="inherit"
          >
      {intl.formatMessage({ id: "addNewCard" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: "subscriptionSuccessful" })}</DialogTitle>
        <DialogContent>
          {confirmingSubscription ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {intl.formatMessage({ id: "processing" })} - Creating subscription and refreshing data...
              </Typography>
            </Box>
          ) : (
            <Box py={2}>
              <Typography variant="body1" paragraph>
                Your subscription to <Typography 
  component="span" 
  sx={{ 
    fontWeight: 700,  // Bold like <strong>
    color: theme => theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
  }}
>
  {currentPlan.Product_Name}
</Typography> {intl.formatMessage({ id: "subscriptionSuccessDetails" })}
              </Typography>
              <Typography variant="body1">
             {intl.formatMessage({ id: "accessAllFeatures" })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmSubscription}
            color="primary"
            variant="contained"
            disabled={confirmingSubscription}
            startIcon={confirmingSubscription ? <CircularProgress size={20} /> : null}
          >
            {confirmingSubscription ? 'Confirming & Refreshing...' : "Confirm"}
          </Button>
          <Button
            onClick={() => setShowSuccessDialog(false)}
            color="inherit"
            disabled={confirmingSubscription}
          >
          {intl.formatMessage({ id: "close" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showEditCardDialog}
        onClose={() => setShowEditCardDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: "editCardDetails" })}</DialogTitle>
        <DialogContent>
          <DialogForm>
            <TextField
              label={intl.formatMessage({ id: "cardNumber" })}
              name="number"
              value={newCard.number}
              onChange={handleCardInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label={intl.formatMessage({ id: "cardholderName" })}
              name="name"
              value={newCard.name}
              onChange={handleCardInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label={intl.formatMessage({ id: "expiryMonth" })}
              name="exp_month"
              value={newCard.exp_month}
              onChange={handleCardInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label={intl.formatMessage({ id: "expiryYear" })}
              name="exp_year"
              value={newCard.exp_year}
              onChange={handleCardInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label={intl.formatMessage({ id: "postalCode" })}
              name="postal_code"
              value={newCard.postal_code}
              onChange={handleCardInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label="CVC"
              name="cvc"
              value={newCard.cvc}
              onChange={handleCardInputChange}
              fullWidth
              margin="dense"
            />
          </DialogForm>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditCardDialog(false)} color="inherit">
             {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button onClick={handleSaveCard} color="primary" variant="contained">
        {intl.formatMessage({ id: "saveChanges" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default SubscriptionNew;